import React, { useCallback, useImperativeHandle, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import {
  FilteredPropertyData,
  isMenuSingleProperty,
  MenuMultiProperty,
  MenuSingleProperty
} from '../../../types/PropertyData'
import Overlay, { OverlayRef, OverlaySection, OverlaySubsection } from '../../components/Overlay'
import MultipleChoice from '../../components/propertyControls/MultipleChoice'
import OptionsCircle from '../../components/propertyControls/OptionsCircle'
import OptionsDetailed from '../../components/propertyControls/OptionsDetailed'
import { setExpertPropertiesAction } from '../../store/actions/configurationActions'
import { useSelector } from '../../store/hooks'
import { useGlobalProperties } from '../../store/hooks/dataHooks'
import { usePropertyOverlay } from '../../store/hooks/uiExpertHooks'

const SinglePropertyControl: React.FC<{
  menuProperty: MenuSingleProperty
  properties: FilteredPropertyData[]
  configurationProperties: Record<string, string>
  setConfigurationProperties: (nextProperties: Record<string, string>) => unknown
}> = ({ menuProperty: { controlType, property }, properties, configurationProperties, setConfigurationProperties }) => {
  const propertyData = properties.find((x) => x.id === property)
  const value = configurationProperties[property] ?? ''
  const setValue = useCallback(
    (nextValue: string) => {
      setConfigurationProperties({ [property]: nextValue })
    },
    [property, setConfigurationProperties]
  )

  if (!propertyData) {
    return <></>
  }

  return (
    <OverlaySubsection title={propertyData.displayName}>
      {controlType === 'options-detailed' && (
        <OptionsDetailed propertyData={propertyData} value={value} setValue={setValue} />
      )}
      {controlType === 'options-circle' && (
        <OptionsCircle propertyData={propertyData} value={value} setValue={setValue} />
      )}
    </OverlaySubsection>
  )
}

const MultiPropertyControl: React.FC<{
  menuProperty: MenuMultiProperty
  properties: FilteredPropertyData[]
  configurationProperties: Record<string, string>
  setConfigurationProperties: (nextProperties: Record<string, string>) => unknown
}> = ({
  menuProperty: { controlType, displayName, properties: propertyNames },
  properties,
  configurationProperties,
  setConfigurationProperties
}) => {
  const propertyDatas = useMemo(
    () => propertyNames.map((id) => properties.find((x) => x.id === id)).filter((x): x is FilteredPropertyData => !!x),
    [properties, propertyNames]
  )
  const value = propertyDatas.map((x) => x.id).filter((id) => configurationProperties[id] === 'true')
  const setValue = useCallback(
    (nextValue: string[]) => {
      setConfigurationProperties(
        Object.fromEntries(propertyDatas.map(({ id }) => [id, nextValue.includes(id) ? 'true' : 'false']))
      )
    },
    [propertyDatas, setConfigurationProperties]
  )

  return (
    <OverlaySubsection title={displayName}>
      {controlType === 'multiple-choice' && (
        <MultipleChoice propertyDatas={propertyDatas} value={value} setValue={setValue} />
      )}
    </OverlaySubsection>
  )
}

export type GlobalPropertyOverlayRef = {
  scrollToSection: (name: string) => void
}
const GlobalPropertyOverlay = React.forwardRef<GlobalPropertyOverlayRef>(function GlobalPropertyOverlay(props, ref) {
  const sections = useSelector((state) => state.data.menu) ?? []
  const properties = useGlobalProperties()
  const { globalSection, setGlobalSection } = usePropertyOverlay()
  const idPrefix = 'global-properties-'
  const overlay = useRef<OverlayRef>(null)
  useImperativeHandle(
    ref,
    () => ({
      scrollToSection: (name) => {
        overlay.current?.scrollToSection('global-properties-' + name, true)
      }
    }),
    []
  )

  const dispatch = useDispatch()
  const configurationProperties = useSelector((state) => state.configuration.expertMode.properties)
  const setConfigurationProperties = useCallback(
    (nextProperties: Record<string, string>) => {
      dispatch(setExpertPropertiesAction('global', nextProperties))
    },
    [dispatch]
  )

  const setCurrentSection = useCallback((section: string) => setGlobalSection(section.substring(idPrefix.length)), [
    setGlobalSection
  ])

  return (
    <Overlay
      visible={!!globalSection}
      shaded
      currentSection={globalSection ? idPrefix + globalSection : undefined}
      setCurrentSection={setCurrentSection}
      ref={overlay}
    >
      {sections.map(({ id, displayName, contents }) => (
        <OverlaySection key={id} $title={displayName} $bigTitle id={idPrefix + id} $fluid>
          {contents.map((menuProperty) =>
            isMenuSingleProperty(menuProperty) ? (
              <SinglePropertyControl
                key={menuProperty.property}
                menuProperty={menuProperty}
                properties={properties}
                configurationProperties={configurationProperties}
                setConfigurationProperties={setConfigurationProperties}
              />
            ) : (
              <MultiPropertyControl
                key={menuProperty.properties.join('-')}
                menuProperty={menuProperty}
                properties={properties}
                configurationProperties={configurationProperties}
                setConfigurationProperties={setConfigurationProperties}
              />
            )
          )}
        </OverlaySection>
      ))}
    </Overlay>
  )
})

export default GlobalPropertyOverlay
